import { useMediaQuery } from "@gluestack-ui/utils/hooks";
import { DrawerActions, useNavigation } from "expo-router/react-navigation";
import { Menu } from "lucide-react-native";
import { FC, ReactNode } from "react";

import { useLocalization } from "@/hooks";

import { Box, Heading, HStack, Pressable, Text, VStack } from "./base";

interface HeaderProps {
  /**
   * Set on screens that sit under a Stack back button (pushed screens). Drops
   * the top padding (the back-button bar already provides the top clearance)
   * and the mobile hamburger (the back button owns navigation) so the spacing
   * stays uniform with the other back-button navigators.
   */
  nested?: boolean;
  right?: ReactNode;
  subtitle?: string;
  title: string;
}

const Header: FC<HeaderProps> = ({
  nested = false,
  subtitle,
  title,
  right,
}) => {
  const { i18n } = useLocalization();
  // Pinned to Tailwind's lg (1024px) so this matches the CSS `lg:` classes and
  // the drawer-mode decision in the dashboard layout.
  const [isDesktop] = useMediaQuery({ minWidth: 1024 });
  const navigation = useNavigation();

  // The Header is rendered inside screens that may sit behind nested stack
  // navigators (e.g. studies/[studyId]). Dispatching DrawerActions.openDrawer()
  // bubbles up through parent navigators until the Drawer handles it, so this
  // reaches the drawer from both top-level and nested stack screens.
  const openDrawer = () => navigation.dispatch(DrawerActions.openDrawer());

  return (
    <HStack
      className={`justify-between w-full px-4 lg:px-8 pb-6 ${nested ? "pt-2" : "pt-8"}`}
    >
      <HStack className="flex-1 min-w-0 items-center gap-3">
        {!isDesktop && !nested && (
          <Pressable
            accessibilityLabel={i18n.t("menu.openMenu")}
            accessibilityRole="button"
            className="p-1"
            onPress={openDrawer}
          >
            <Menu
              className="text-typography-900 dark:text-typography-50"
              size={24}
            />
          </Pressable>
        )}
        <VStack className="flex-1 min-w-0 space-y-2">
          <Heading
            className="text-3xl font-bold text-typography-900 dark:text-typography-50"
            numberOfLines={1}
          >
            {title}
          </Heading>
          {subtitle && isDesktop && (
            <Text
              className="text-md text-typography-600 dark:text-typography-400"
              numberOfLines={1}
            >
              {subtitle}
            </Text>
          )}
        </VStack>
      </HStack>
      {right && <Box className="shrink-0">{right}</Box>}
    </HStack>
  );
};

export default Header;
